/**
 * Staged card / cash / invoice allocation for the work-order invoice dialog (#1999, #2024).
 *
 * Invoice is not a tender — it is the open remainder. Cash and invoice each appear
 * at most once. Card may repeat. Checkout tenders (card + cash) are capped to match
 * staff-api (MAX_CHECKOUT_TENDERS).
 */

import type { CheckoutTender } from '@/hooks/useInvoices';

export type PaymentSplitMethod = 'card' | 'cash' | 'invoice';

export const PAYMENT_SPLIT_METHODS: readonly PaymentSplitMethod[] = ['card', 'cash', 'invoice'];

/** Matches staff-api checkout: at most this many card+cash tenders. */
export const MAX_CHECKOUT_TENDERS = 5;

export interface PaymentSplitRow {
  id: string;
  method: PaymentSplitMethod;
  amountCents: number;
}

export interface SplitTenderDetails {
  readonly cardReferences: Readonly<Record<string, string>>;
  readonly cashReceivedCents: number;
}

/** Historical generateInvoice default; also the "closest to" target for the dropdown. */
export const FALLBACK_PAYMENT_TERMS_DAYS = 14;

let nextSplitRowSeq = 0;

function newPaymentSplitRowId(): string {
  nextSplitRowSeq += 1;
  return `split-${nextSplitRowSeq}`;
}

function createSplitRow(method: PaymentSplitMethod, amountCents: number): PaymentSplitRow {
  return { id: newPaymentSplitRowId(), method, amountCents };
}

export function checkoutTenderCount(rows: readonly PaymentSplitRow[]): number {
  return rows.filter((row) => row.method === 'card' || row.method === 'cash').length;
}

export function createInitialSplit(totalCents: number): PaymentSplitRow[] {
  return [createSplitRow('card', Math.max(0, totalCents))];
}

export function allocatedCents(rows: readonly PaymentSplitRow[]): number {
  return rows.reduce((sum, row) => sum + row.amountCents, 0);
}

export function unallocatedCents(totalCents: number, rows: readonly PaymentSplitRow[]): number {
  return totalCents - allocatedCents(rows);
}

export function invoiceRemainderCents(rows: readonly PaymentSplitRow[]): number {
  return rows
    .filter((row) => row.method === 'invoice')
    .reduce((sum, row) => sum + row.amountCents, 0);
}

export function availableMethodsForRow(
  rows: readonly PaymentSplitRow[],
  index: number,
): PaymentSplitMethod[] {
  const others = rows.filter((_, rowIndex) => rowIndex !== index);
  const used = new Set(others.map((row) => row.method));
  const canBeTender = checkoutTenderCount(others) < MAX_CHECKOUT_TENDERS;
  return PAYMENT_SPLIT_METHODS.filter((method) => {
    if (method === 'card') return canBeTender;
    if (method === 'cash') return !used.has('cash') && canBeTender;
    return !used.has('invoice');
  });
}

export function firstUnusedMethod(rows: readonly PaymentSplitRow[]): PaymentSplitMethod | null {
  const used = new Set(rows.map((row) => row.method));
  const canTender = checkoutTenderCount(rows) < MAX_CHECKOUT_TENDERS;
  for (const method of PAYMENT_SPLIT_METHODS) {
    if (used.has(method)) continue;
    if (method === 'invoice') return 'invoice';
    if (canTender) return method;
  }
  return null;
}

export function maxAmountCentsForRow(
  totalCents: number,
  rows: readonly PaymentSplitRow[],
  index: number,
): number {
  let used = 0;
  for (let i = 0; i < index; i += 1) {
    used += rows[i]?.amountCents ?? 0;
  }
  return Math.max(0, totalCents - used);
}

export function pickDefaultPaymentTermsDays(options: readonly number[]): number {
  if (options.length === 0) return FALLBACK_PAYMENT_TERMS_DAYS;
  const first = options[0];
  if (first === undefined) return FALLBACK_PAYMENT_TERMS_DAYS;
  if (options.includes(FALLBACK_PAYMENT_TERMS_DAYS)) return FALLBACK_PAYMENT_TERMS_DAYS;
  let best = first;
  let bestDist = Math.abs(best - FALLBACK_PAYMENT_TERMS_DAYS);
  for (const value of options) {
    const dist = Math.abs(value - FALLBACK_PAYMENT_TERMS_DAYS);
    if (dist < bestDist) {
      best = value;
      bestDist = dist;
    }
  }
  return best;
}

export function dueDateIsoFromPaymentTermsDays(days: number, from: Date = new Date()): string {
  return new Date(from.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}

export function applyMethodChange(
  rows: readonly PaymentSplitRow[],
  index: number,
  method: PaymentSplitMethod,
): PaymentSplitRow[] {
  if (index < 0 || index >= rows.length) return [...rows];
  const current = rows[index];
  if (!current) return [...rows];
  if (current.method === method) return [...rows];
  if (method !== 'card' && rows.some((row, rowIndex) => rowIndex !== index && row.method === method)) {
    return [...rows];
  }
  const others = rows.filter((_, rowIndex) => rowIndex !== index);
  const wouldBeTender = method === 'card' || method === 'cash';
  if (wouldBeTender && checkoutTenderCount(others) >= MAX_CHECKOUT_TENDERS) {
    return [...rows];
  }
  return rows.map((row, rowIndex) => (rowIndex === index ? { ...row, method } : row));
}

export function applyAmountChange(
  totalCents: number,
  rows: readonly PaymentSplitRow[],
  index: number,
  nextAmountCents: number,
): PaymentSplitRow[] {
  if (index < 0 || index >= rows.length) return [...rows];
  const max = maxAmountCentsForRow(totalCents, rows, index);
  const amount = Math.min(Math.max(0, Math.trunc(nextAmountCents)), max);
  const updated = rows.map((row, rowIndex) => (
    rowIndex === index ? { ...row, amountCents: amount } : row
  ));
  const isLast = index === rows.length - 1;
  if (isLast) {
    const leftover = max - amount;
    if (leftover > 0 && amount > 0) {
      const method = firstUnusedMethod(updated);
      if (method) {
        return [...updated, createSplitRow(method, leftover)];
      }
    }
    return updated;
  }
  return rebalanceTrailing(totalCents, updated, index);
}

/**
 * Whether another payment row can be added (#2209).
 *
 * The split always sums to the total — every edit rebalances the leftover onto
 * the last row — so there is never an unallocated remainder to hand a new row.
 * What limits adding is the tender cap staff-api enforces, and having something
 * on the last row to split off.
 */
export function canAddSplitRow(rows: readonly PaymentSplitRow[]): boolean {
  if (checkoutTenderCount(rows) >= MAX_CHECKOUT_TENDERS) return false;
  const last = rows[rows.length - 1];
  return last !== undefined && last.amountCents > 0;
}

/**
 * Split the last row in two (#2209).
 *
 * The new row INHERITS the last row's method, which is the point: two card
 * payments are legal (staff-api allows repeated cards since #2070) but nothing
 * offered them. The row `applyAmountChange` spawns takes `firstUnusedMethod`
 * instead, so shrinking an amount can never produce a second card — the user
 * had to accept a cash row and switch it by hand.
 *
 * The amount comes off the last row rather than from a remainder, because the
 * split is always fully allocated. Half, rounded so the two halves still sum to
 * the original.
 *
 * Invoice is the open remainder rather than a tender, so inheriting it would
 * make a second remainder row; card is the sensible default there. Cash may
 * appear only once, so it falls back to card too.
 */
export function applyAddRow(rows: readonly PaymentSplitRow[]): PaymentSplitRow[] {
  if (!canAddSplitRow(rows)) return [...rows];
  const lastIndex = rows.length - 1;
  const last = rows[lastIndex];
  if (!last) return [...rows];

  const moved = Math.floor(last.amountCents / 2);
  if (moved <= 0) return [...rows];

  const inherited: PaymentSplitMethod = last.method === 'invoice' ? 'card' : last.method;
  const method: PaymentSplitMethod =
    inherited === 'cash' && rows.some((row) => row.method === 'cash') ? 'card' : inherited;

  return [
    ...rows.slice(0, lastIndex),
    { ...last, amountCents: last.amountCents - moved },
    createSplitRow(method, moved),
  ];
}

export function applyRemoveRow(
  totalCents: number,
  rows: readonly PaymentSplitRow[],
  index: number,
): PaymentSplitRow[] {
  if (rows.length <= 1 || index < 0 || index >= rows.length) return [...rows];
  return rebalanceAll(totalCents, rows.filter((_, rowIndex) => rowIndex !== index));
}

export function toCheckoutTenders(
  rows: readonly PaymentSplitRow[],
  details: SplitTenderDetails,
): CheckoutTender[] {
  const tenders: CheckoutTender[] = [];
  for (const row of rows) {
    if (row.amountCents <= 0) continue;
    if (row.method === 'card') {
      tenders.push({
        method: 'card',
        amountCents: row.amountCents,
        paymentReference: details.cardReferences[row.id] ?? '',
      });
    } else if (row.method === 'cash') {
      tenders.push({
        method: 'cash',
        amountCents: row.amountCents,
        cashReceivedCents: details.cashReceivedCents,
      });
    }
  }
  return tenders;
}

function rebalanceTrailing(
  totalCents: number,
  rows: readonly PaymentSplitRow[],
  changedIndex: number,
): PaymentSplitRow[] {
  const head = rows.slice(0, changedIndex + 1);
  const tail = rows.slice(changedIndex + 1);
  let remaining = totalCents - allocatedCents(head);
  const nextTail: PaymentSplitRow[] = [];
  for (let i = 0; i < tail.length; i += 1) {
    const row = tail[i];
    if (!row || remaining <= 0) break;
    const isLastTail = i === tail.length - 1;
    if (isLastTail) {
      nextTail.push({ ...row, amountCents: remaining });
      remaining = 0;
      break;
    }
    const amount = Math.min(row.amountCents, remaining);
    if (amount <= 0) break;
    nextTail.push({ ...row, amountCents: amount });
    remaining -= amount;
  }
  if (remaining > 0) {
    const last = nextTail[nextTail.length - 1];
    if (last) {
      nextTail[nextTail.length - 1] = { ...last, amountCents: last.amountCents + remaining };
      remaining = 0;
    } else {
      const method = firstUnusedMethod(head);
      if (method) {
        nextTail.push(createSplitRow(method, remaining));
        remaining = 0;
      }
    }
  }
  return [...head, ...nextTail];
}

function rebalanceAll(totalCents: number, rows: readonly PaymentSplitRow[]): PaymentSplitRow[] {
  if (rows.length === 0) return createInitialSplit(totalCents);
  const last = rows[rows.length - 1];
  if (!last) return createInitialSplit(totalCents);
  if (rows.length === 1) {
    return [{ ...last, amountCents: Math.max(0, totalCents) }];
  }
  const head = rows.slice(0, -1);
  let used = 0;
  const clampedHead: PaymentSplitRow[] = [];
  for (const row of head) {
    const remaining = totalCents - used;
    if (remaining <= 0) break;
    const amount = Math.min(row.amountCents, remaining);
    if (amount <= 0) break;
    clampedHead.push({ ...row, amountCents: amount });
    used += amount;
  }
  const remaining = totalCents - used;
  if (remaining <= 0) {
    return clampedHead.length > 0 ? clampedHead : createInitialSplit(totalCents);
  }
  return [...clampedHead, { ...last, amountCents: remaining }];
}
